/**
 * ResultsPage
 * ===========
 * Tabbed results view. The active tab and selected exam live in the URL
 * (?tab=&exam=) so a refresh keeps you on the same view.
 *   Tabs: table | summary | settings
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ExamList from '../components/results/ExamList';
import ResultTable from '../components/results/ResultTable';
import ResultSummary from '../components/results/ResultSummary';
import ReportCardsTab from '../components/results/ReportCardsTab';
import GradeSettings from '../components/results/GradeSettings';
import { fetchExams, fetchExamResults, fetchGrades } from '../components/results/resultsApi';
import '../components/results/Results.css';

const TABS = [
  { id: 'table', label: 'Result Table' },
  { id: 'summary', label: 'Summary' },
  { id: 'cards', label: 'Report Cards' },
  { id: 'settings', label: 'Grade Settings' },
];

const ResultsPage = () => {
  const { schoolId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'table';
  const examId = searchParams.get('exam') || '';

  const [exams, setExams] = useState([]);
  const [examData, setExamData] = useState(null);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedExam = useMemo(
    () => exams.find((e) => e.Exam_Id === examId) || null,
    [exams, examId]
  );

  // Load exams + grade bands once
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [examList, gradeData] = await Promise.all([
          fetchExams(schoolId),
          fetchGrades(schoolId),
        ]);
        if (!active) return;
        setExams(examList || []);
        setGrades(gradeData?.grades || []);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [schoolId]);

  // Fetch results whenever an exam is selected (shared by table + summary + cards tabs)
  useEffect(() => {
    if (!examId || tab === 'settings') return;
    if (examData && examData.exam?.Exam_Id === examId) return;

    let active = true;
    (async () => {
      setTableLoading(true);
      setError('');
      try {
        const data = await fetchExamResults(schoolId, examId);
        if (active) setExamData(data);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setTableLoading(false);
      }
    })();
    return () => { active = false; };
  }, [schoolId, examId, tab, examData]);

  const setTab = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    });
  }, [setSearchParams]);

  const handleSelectExam = useCallback((exam) => {
    setExamData(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('exam', exam.Exam_Id);
      if (next.get('tab') === 'settings') next.set('tab', 'table');
      return next;
    });
  }, [setSearchParams]);

  const handleBack = useCallback(() => {
    setExamData(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('exam');
      return next;
    });
  }, [setSearchParams]);

  // refetch current exam results (e.g. after a marks edit)
  const handleMarksSaved = useCallback(() => setExamData(null), []);

  const needsExam = tab !== 'settings';

  const renderTabContent = () => {
    if (tab === 'settings') {
      return <GradeSettings schoolId={schoolId} />;
    }

    if (!selectedExam) {
      return <ExamList exams={exams} onSelect={handleSelectExam} />;
    }
    if (tableLoading) {
      return (
        <div className="results-loading">
          <div className="dash-spinner" />
          <span>Loading results...</span>
        </div>
      );
    }
    if (!examData) return null;
    if (tab === 'summary') return <ResultSummary data={examData} grades={grades} schoolId={schoolId} />;
    if (tab === 'cards') return <ReportCardsTab data={examData} grades={grades} schoolId={schoolId} />;
    return <ResultTable data={examData} grades={grades} schoolId={schoolId} onMarksSaved={handleMarksSaved} />;
  };

  return (
    <div className="results-page">
      {/* Header */}
      <div className="results-header">
        {needsExam && selectedExam ? (
          <>
            <button type="button" className="results-back-btn" onClick={handleBack}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Exams
            </button>
            <div>
              <h1 className="page-title">{selectedExam.Exam_Name}</h1>
              <p className="results-subtitle">
                {tab === 'summary' ? 'Performance summary'
                  : tab === 'cards' ? 'Class-wise report cards'
                    : 'Compact result sheet'}
              </p>
            </div>
          </>
        ) : (
          <h1 className="page-title">Results</h1>
        )}
      </div>

      {/* Tabs */}
      <div className="results-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`results-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="results-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {loading ? (
        <div className="results-loading">
          <div className="dash-spinner" />
          <span>Loading...</span>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};

export default ResultsPage;
